import React, { Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SettingsHeader, SettingsMenu } from './components';

// Lazy load heavy components
const AccountPage = React.lazy(() => import('./account').then(m => ({ default: m.AccountPage })));
const AccountsNewPage = React.lazy(() => import('./account').then(m => ({ default: m.AccountsNewPage })));
const AccountsEditPage = React.lazy(() => import('./account').then(m => ({ default: m.AccountsEditPage })));
const AccountsDetailPage = React.lazy(() => import('./account').then(m => ({ default: m.AccountsDetailPage })));
const NotificationsPage = React.lazy(() => import('./notifications').then(m => ({ default: m.NotificationsPage })));
const PermissionsListPage = React.lazy(() => import('./permissions').then(m => ({ default: m.PermissionsListPage })));
const MembersListPage = React.lazy(() => import('./members').then(m => ({ default: m.MembersListPage })));
const MemberDetailPage = React.lazy(() => import('./members').then(m => ({ default: m.MemberDetailPage })));
const MemberNewPage = React.lazy(() => import('./members').then(m => ({ default: m.MemberNewPage })));
const MemberEditPage = React.lazy(() => import('./members').then(m => ({ default: m.MemberEditPage })));
const RolesListPage = React.lazy(() => import('./roles').then(m => ({ default: m.RolesListPage })));
const RolesDetailPage = React.lazy(() => import('./roles').then(m => ({ default: m.RolesDetailPage })));
const RolesNewPage = React.lazy(() => import('./roles').then(m => ({ default: m.RolesNewPage })));
const RolesEditPage = React.lazy(() => import('./roles').then(m => ({ default: m.RolesEditPage })));
const CategoriesListPage = React.lazy(() => import('./categories').then(m => ({ default: m.CategoriesListPage })));
const CategoriesNewPage = React.lazy(() => import('./categories').then(m => ({ default: m.CategoriesNewPage })));
const CategoriesEditPage = React.lazy(() => import('./categories').then(m => ({ default: m.CategoriesEditPage })));
const CategoriesDetailPage = React.lazy(() => import('./categories').then(m => ({ default: m.CategoriesDetailPage })));
const WarehousesListPage = React.lazy(() => import('./warehouses').then(m => ({ default: m.WarehousesListPage })));
const WarehouseDetailPage = React.lazy(() => import('./warehouses').then(m => ({ default: m.WarehouseDetailPage })));
const WarehouseNewPage = React.lazy(() => import('./warehouses').then(m => ({ default: m.WarehouseNewPage })));
const WarehouseEditPage = React.lazy(() => import('./warehouses').then(m => ({ default: m.WarehouseEditPage })));
const PartnersListPage = React.lazy(() => import('./partners').then(m => ({ default: m.PartnersListPage })));
const PartnerDetailPage = React.lazy(() => import('./partners').then(m => ({ default: m.PartnerDetailPage })));
const PartnerNewPage = React.lazy(() => import('./partners').then(m => ({ default: m.PartnerNewPage })));
const PartnerEditPage = React.lazy(() => import('./partners').then(m => ({ default: m.PartnerEditPage })));
const TemplatesListPage = React.lazy(() => import('./templates').then(m => ({ default: m.TemplatesListPage })));
const TemplatesNewPage = React.lazy(() => import('./templates').then(m => ({ default: m.TemplatesNewPage })));
const TemplatesEditPage = React.lazy(() => import('./templates').then(m => ({ default: m.TemplatesEditPage })));
const TemplatesDetailPage = React.lazy(() => import('./templates').then(m => ({ default: m.TemplatesDetailPage })));
const IntegrationsListPage = React.lazy(() => import('./integrations').then(m => ({ default: m.IntegrationsListPage })));
const ActivityListPage = React.lazy(() => import('./activity').then(m => ({ default: m.ActivityListPage })));
const BackupListPage = React.lazy(() => import('./backup').then(m => ({ default: m.BackupListPage })));
const RouterManagementPage = React.lazy(() => import('./router/RouterManagementPage'));
import { GeneralSettingsPage } from './GeneralSettingsPage';

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeMenu = searchParams.get('tab') || 'general';

  const setActiveMenu = (tab: string) => {
    setSearchParams({ tab });
  };

  // Define full-width pages
  const isFullWidthPage = activeMenu.includes('-new') || activeMenu.includes('-edit') || activeMenu.includes('-detail');

  const renderContent = () => {
    switch (activeMenu) {
      case 'account': return <AccountPage />;
      case 'account-new': return <AccountsNewPage />;
      case 'account-edit': return <AccountsEditPage />;
      case 'account-detail': return <AccountsDetailPage />;
      case 'notifications': return <NotificationsPage />;
      case 'permissions': return <PermissionsListPage />;
      case 'members': return <MembersListPage />;
      case 'members-detail': return <MemberDetailPage />;
      case 'members-new': return <MemberNewPage />;
      case 'members-edit': return <MemberEditPage />;
      case 'roles': return <RolesListPage />;
      case 'roles-detail': return <RolesDetailPage />;
      case 'roles-new': return <RolesNewPage />;
      case 'roles-edit': return <RolesEditPage />;
      case 'categories': return <CategoriesListPage />;
      case 'categories-new': return <CategoriesNewPage />;
      case 'categories-edit': return <CategoriesEditPage />;
      case 'categories-detail': return <CategoriesDetailPage />;
      case 'warehouses': return <WarehousesListPage />;
      case 'warehouses-new': return <WarehouseNewPage />;
      case 'warehouses-edit': return <WarehouseEditPage />;
      case 'warehouses-detail': return <WarehouseDetailPage />;
      case 'partners': return <PartnersListPage />;
      case 'partners-new': return <PartnerNewPage />;
      case 'partners-edit': return <PartnerEditPage />;
      case 'partners-detail': return <PartnerDetailPage />;
      case 'templates': return <TemplatesListPage />;
      case 'templates-new': return <TemplatesNewPage />;
      case 'templates-edit': return <TemplatesEditPage />;
      case 'templates-detail': return <TemplatesDetailPage />;
      case 'integrations': return <IntegrationsListPage />;
      case 'activity': return <ActivityListPage />;
      case 'router': return <RouterManagementPage />;
      case 'backup': return <BackupListPage />;
      default: return <GeneralSettingsPage />;
    }
  };

  return (
    <main className="flex flex-col min-h-screen bg-slate-50 p-4 text-slate-800 lg:p-6 flex-1 min-w-0">
      <div className="flex-1 min-w-0 space-y-4">
        {!isFullWidthPage && <SettingsHeader isSaving={false} />}
        
        <div className={isFullWidthPage ? "block" : "grid gap-4 lg:grid-cols-[200px_1fr] xl:grid-cols-[200px_1fr] 2xl:grid-cols-[240px_1fr]"}>
          {!isFullWidthPage && <SettingsMenu activeKey={activeMenu} onChange={setActiveMenu} />}
          
          <Suspense fallback={<div className="h-96 animate-pulse rounded-md bg-white p-3 shadow-sm" />}>
            {renderContent()}
          </Suspense>
        </div>
      </div>
    </main>
  );
}
